import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Phone, Mail, Globe, ChevronRight, Calendar, Users, Check, X, Anchor } from 'lucide-react';
import { Toaster, toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PESCHIERA_LOGO = "https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png";

// ── Booking modal: only phone + num_people ────────────────────
function SpecialBookingModal({ event, onClose }) {
  const [form, setForm] = useState({ first_name: '', phone: '', num_people: 2, notes: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const canSubmit = form.phone.trim().length >= 6 && form.num_people >= 1;

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/special-bookings`, {
        special_event_id: event.id,
        first_name: form.first_name.trim() || null,
        phone: form.phone.trim(),
        num_people: parseInt(form.num_people),
        notes: form.notes.trim() || null,
      });
      setSuccess(res.data);
      toast.success('Richiesta inviata! Ti contatteremo a breve.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante l\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="special-booking-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(3,12,28,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #0c2340 0%, #0a1929 60%, #1a1308 100%)',
          border: '1px solid rgba(251,191,36,0.35)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.6), 0 0 40px rgba(251,191,36,0.15)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="close-special-modal"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-colors z-10"
          style={{ background: 'rgba(255,255,255,0.08)', color: '#fbbf24' }}
        >
          <X size={18} />
        </button>

        {success ? (
          <div className="p-8 text-center" data-testid="special-booking-success">
            <div
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5"
              style={{
                background: 'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(245,158,11,0.18))',
                border: '2px solid #fbbf24',
                boxShadow: '0 0 30px rgba(251,191,36,0.4)',
              }}
            >
              <Check size={36} style={{ color: '#fbbf24' }} strokeWidth={2.5} />
            </div>
            <h3 className="font-playfair text-2xl font-bold text-white mb-2">Richiesta ricevuta!</h3>
            <p className="text-gray-300 font-manrope text-sm mb-1">Il tuo codice prenotazione</p>
            <p
              className="font-playfair text-2xl font-bold tracking-widest mb-5"
              data-testid="special-booking-code"
              style={{ color: '#fbbf24' }}
            >
              {success.booking_code}
            </p>
            <p className="text-gray-400 font-manrope text-sm leading-relaxed">
              Ti ricontatteremo al numero <span className="text-white font-600">{success.phone}</span> per finalizzare la prenotazione di {success.num_people} {success.num_people === 1 ? 'persona' : 'persone'}.
            </p>
            <button
              onClick={onClose}
              className="mt-7 w-full py-3 rounded-full font-manrope font-700 uppercase tracking-widest text-sm transition-all"
              style={{
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: '#0a1929',
              }}
            >
              Chiudi
            </button>
          </div>
        ) : (
          <div className="p-7 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <Anchor size={22} style={{ color: '#fbbf24' }} />
              <p className="font-manrope font-700 uppercase tracking-[0.25em] text-xs" style={{ color: '#fbbf24' }}>
                Richiedi info & prenota
              </p>
            </div>
            <h3 className="font-playfair text-2xl font-bold text-white leading-tight mb-2">{event.title}</h3>
            <p className="text-gray-400 font-manrope text-sm mb-7">{event.date_range}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-manrope font-700 uppercase tracking-wider mb-1.5" style={{ color: '#fbbf24' }}>
                  Nome <span className="text-gray-500 normal-case tracking-normal">(opzionale)</span>
                </label>
                <input
                  type="text"
                  data-testid="special-input-name"
                  value={form.first_name}
                  onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                  placeholder="Mario"
                  className="w-full px-4 py-3 rounded-xl font-manrope text-base text-white outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(251,191,36,0.2)',
                  }}
                  onFocus={e => e.target.style.borderColor = '#fbbf24'}
                  onBlur={e => e.target.style.borderColor = 'rgba(251,191,36,0.2)'}
                />
              </div>

              <div>
                <label className="block text-xs font-manrope font-700 uppercase tracking-wider mb-1.5" style={{ color: '#fbbf24' }}>
                  Numero di telefono *
                </label>
                <input
                  type="tel"
                  data-testid="special-input-phone"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="333 1234567"
                  className="w-full px-4 py-3 rounded-xl font-manrope text-base text-white outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(251,191,36,0.2)',
                  }}
                  onFocus={e => e.target.style.borderColor = '#fbbf24'}
                  onBlur={e => e.target.style.borderColor = 'rgba(251,191,36,0.2)'}
                />
              </div>

              <div>
                <label className="block text-xs font-manrope font-700 uppercase tracking-wider mb-1.5" style={{ color: '#fbbf24' }}>
                  Numero di persone *
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, num_people: Math.max(1, f.num_people - 1) }))}
                    data-testid="special-people-minus"
                    className="w-12 h-12 rounded-full font-bold text-xl flex items-center justify-center transition-all"
                    style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
                  >
                    −
                  </button>
                  <div
                    className="flex-1 text-center py-3 rounded-xl font-playfair font-bold text-2xl text-white"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(251,191,36,0.2)' }}
                    data-testid="special-people-count"
                  >
                    {form.num_people}
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, num_people: Math.min(20, f.num_people + 1) }))}
                    data-testid="special-people-plus"
                    className="w-12 h-12 rounded-full font-bold text-xl flex items-center justify-center transition-all"
                    style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <p data-testid="special-booking-error" className="text-red-400 font-manrope text-sm mt-4">{error}</p>
            )}

            <button
              onClick={submit}
              disabled={loading || !canSubmit}
              data-testid="special-submit-btn"
              className="w-full mt-7 py-4 rounded-full font-manrope font-800 uppercase tracking-[0.15em] text-sm transition-all flex items-center justify-center gap-2"
              style={{
                background: canSubmit
                  ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                  : 'rgba(255,255,255,0.06)',
                color: canSubmit ? '#0a1929' : '#666',
                boxShadow: canSubmit ? '0 10px 30px rgba(251,191,36,0.35)' : 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {loading ? 'Invio...' : 'INVIA RICHIESTA'}
              {!loading && <ChevronRight size={18} />}
            </button>
            <p className="text-gray-500 font-manrope text-xs text-center mt-3">
              Ti contatteremo telefonicamente per i dettagli e il pagamento.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Special Event Card ────────────────────────────────────────
function SpecialEventCard({ event, onBook }) {
  return (
    <article
      data-testid={`special-event-${event.id}`}
      className="relative rounded-3xl overflow-hidden mb-12 last:mb-0"
      style={{
        background: 'linear-gradient(155deg, rgba(12,35,64,0.6) 0%, rgba(10,25,41,0.85) 60%, rgba(26,19,8,0.8) 100%)',
        border: '1px solid rgba(251,191,36,0.25)',
        boxShadow: '0 25px 80px rgba(0,0,0,0.45), 0 0 1px rgba(251,191,36,0.4) inset',
      }}
    >
      {/* Sunset glow */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: '-30%', right: '-15%', width: '60%', height: '120%',
          background: 'radial-gradient(circle, rgba(251,146,60,0.18) 0%, transparent 65%)',
        }}
      />
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          bottom: '-20%', left: '-10%', width: '50%', height: '80%',
          background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 60%)',
        }}
      />

      <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-0">
        {/* Poster image */}
        <div className="relative overflow-hidden" style={{ background: '#040d1a', minHeight: '320px' }}>
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={event.title}
              data-testid={`special-event-image-${event.id}`}
              className="w-full h-full object-cover"
              style={{ minHeight: '380px' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ minHeight: '380px' }}>
              <Anchor size={72} style={{ color: 'rgba(251,191,36,0.3)' }} />
            </div>
          )}
          {event.sold_out && (
            <div
              className="absolute top-5 left-5 px-4 py-2 rounded-full font-manrope font-800 uppercase tracking-widest text-xs"
              style={{ background: '#ef4444', color: '#fff' }}
              data-testid={`special-soldout-${event.id}`}
            >
              SOLD OUT
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-7 md:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-3">
            <Anchor size={16} style={{ color: '#fbbf24' }} />
            <p className="font-manrope font-700 uppercase tracking-[0.3em] text-[11px]" style={{ color: '#fbbf24' }}>
              Evento speciale
            </p>
          </div>
          <h2
            className="font-playfair font-900 text-white leading-[1.05] mb-3"
            data-testid={`special-event-title-${event.id}`}
            style={{
              fontSize: 'clamp(1.85rem, 3.4vw, 2.8rem)',
              textShadow: '0 0 30px rgba(251,191,36,0.25)',
            }}
          >
            {event.title}
          </h2>
          {event.subtitle && (
            <p className="text-gray-300 font-manrope text-base md:text-lg mb-5">{event.subtitle}</p>
          )}

          <div className="flex items-center gap-2 mb-6">
            <Calendar size={18} style={{ color: '#fbbf24' }} />
            <span className="font-manrope font-700 text-white text-base md:text-lg" data-testid={`special-event-date-${event.id}`}>
              {event.date_range}
            </span>
          </div>

          {event.description && (
            <p className="text-gray-400 font-manrope text-sm md:text-base leading-relaxed mb-7">
              {event.description}
            </p>
          )}

          {/* Cosa è incluso */}
          {event.included_items && event.included_items.length > 0 && (
            <div
              data-testid={`special-included-${event.id}`}
              className="mb-7 p-5 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(251,191,36,0.06), rgba(251,191,36,0.02))',
                border: '1px solid rgba(251,191,36,0.2)',
              }}
            >
              <p
                className="font-manrope font-700 uppercase tracking-[0.25em] text-xs mb-4"
                style={{ color: '#fbbf24' }}
              >
                ◆ Cosa è incluso
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {event.included_items.map((it, idx) => (
                  <div
                    key={idx}
                    data-testid={`special-included-item-${event.id}-${idx}`}
                    className="flex items-center gap-2.5"
                  >
                    <span style={{ fontSize: '1.4rem' }} className="shrink-0">{it.icon}</span>
                    <span className="font-manrope text-sm md:text-[15px] text-white leading-tight">
                      {it.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {event.bookings_disabled ? (
            <div
              data-testid={`special-view-only-${event.id}`}
              className="inline-flex items-center gap-2 self-start px-5 py-3 rounded-full font-manrope font-700 uppercase tracking-[0.2em] text-xs"
              style={{
                background: 'rgba(6,182,212,0.1)',
                border: '1px solid rgba(6,182,212,0.4)',
                color: '#22d3ee',
              }}
            >
              <span style={{ fontSize: '1rem' }}>👁</span>
              Solo annuncio — info presto
            </div>
          ) : (
            <button
              onClick={() => onBook(event)}
              disabled={event.sold_out}
              data-testid={`special-cta-${event.id}`}
              className="group inline-flex items-center justify-center gap-3 self-start font-manrope font-800 uppercase tracking-[0.18em] text-sm md:text-base rounded-full transition-all duration-300"
              style={{
                padding: '1rem 2.2rem',
                background: event.sold_out
                  ? 'rgba(239,68,68,0.15)'
                  : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: event.sold_out ? '#ef4444' : '#0a1929',
                boxShadow: event.sold_out ? 'none' : '0 12px 36px rgba(251,191,36,0.4)',
                cursor: event.sold_out ? 'not-allowed' : 'pointer',
                opacity: event.sold_out ? 0.6 : 1,
              }}
              onMouseEnter={e => {
                if (!event.sold_out) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 18px 44px rgba(251,191,36,0.55)';
                }
              }}
              onMouseLeave={e => {
                if (!event.sold_out) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 36px rgba(251,191,36,0.4)';
                }
              }}
            >
              {event.sold_out ? 'SOLD OUT' : (event.cta_label || 'PRENOTA ORA')}
              {!event.sold_out && <ChevronRight size={20} className="transition-transform group-hover:translate-x-1" />}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function SpecialEventsPage() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [bookingEvent, setBookingEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/special-events`)
      .then(r => { setEvents(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  return (
    <div
      data-testid="special-events-page"
      style={{
        background: 'radial-gradient(ellipse at 80% 10%, rgba(251,146,60,0.10) 0%, transparent 50%), radial-gradient(ellipse at 10% 90%, rgba(6,182,212,0.08) 0%, transparent 55%), linear-gradient(180deg, #050d1a 0%, #0a1929 60%, #050d1a 100%)',
        minHeight: '100vh',
        color: '#fff',
      }}
    >
      <Toaster position="top-right" />

      {/* Top bar */}
      <header
        data-testid="special-topbar"
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          backdropFilter: 'blur(16px)',
          background: 'rgba(5,13,26,0.78)',
          borderBottom: '1px solid rgba(251,191,36,0.15)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <a
            href="/"
            data-testid="special-back-link"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors font-manrope font-600 text-sm"
          >
            <ArrowLeft size={18} /> <span className="hidden sm:inline">La Peschiera</span>
          </a>
          <div className="flex items-center gap-3">
            <Anchor size={20} style={{ color: '#fbbf24' }} />
            <span className="font-playfair font-bold text-white text-lg tracking-wide">
              Eventi Speciali
            </span>
          </div>
          <div style={{ width: '60px' }} />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-14 pb-10 md:pt-20 md:pb-14">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <p
            className="font-manrope font-700 uppercase tracking-[0.4em] text-xs mb-5"
            style={{ color: '#fbbf24' }}
            data-testid="special-eyebrow"
          >
            ◆ Viaggi & Esperienze esclusive ◆
          </p>
          <h1
            className="font-playfair font-900 text-white mb-5 leading-[1.05]"
            style={{
              fontSize: 'clamp(2.4rem, 5.5vw, 4.2rem)',
              textShadow: '0 0 40px rgba(251,191,36,0.2), 0 0 12px rgba(251,146,60,0.15)',
            }}
            data-testid="special-page-title"
          >
            Eventi Speciali
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #fbbf24 0%, #fb923c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              de La Peschiera
            </span>
          </h1>
          <p className="text-gray-400 font-manrope text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Crociere, viaggi-evento e esperienze indimenticabili pensate per i nostri clienti. Posti sempre limitati — prenota in anticipo.
          </p>

          {/* Decorative wave divider */}
          <div className="flex items-center justify-center gap-3 mt-9">
            <span className="h-px w-12 md:w-24" style={{ background: 'linear-gradient(90deg, transparent, #fbbf24)' }} />
            <Anchor size={18} style={{ color: '#fbbf24' }} />
            <span className="h-px w-12 md:w-24" style={{ background: 'linear-gradient(90deg, #fbbf24, transparent)' }} />
          </div>
        </div>
      </section>

      {/* Events list */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-24">
        {!loaded ? (
          <div className="flex justify-center py-16">
            <div className="w-12 h-12 rounded-full border-2 border-amber-400/30 border-t-amber-400 animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16" data-testid="special-no-events">
            <Anchor size={48} style={{ color: 'rgba(251,191,36,0.3)' }} className="mx-auto mb-4" />
            <p className="font-playfair text-2xl text-white mb-2">Nessun evento speciale in programma</p>
            <p className="text-gray-400 font-manrope">Torna presto per scoprire le prossime esperienze.</p>
          </div>
        ) : (
          events.map(ev => (
            <SpecialEventCard key={ev.id} event={ev} onBook={setBookingEvent} />
          ))
        )}
      </section>

      {/* Booking modal */}
      {bookingEvent && (
        <SpecialBookingModal event={bookingEvent} onClose={() => setBookingEvent(null)} />
      )}

      {/* Footer */}
      <footer
        data-testid="special-footer"
        style={{ background: '#030b18', borderTop: '1px solid rgba(251,191,36,0.12)', padding: '36px 16px' }}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
          <img src={PESCHIERA_LOGO} alt="La Peschiera Club" className="h-9 object-contain opacity-90" />
          <div className="flex items-center gap-5 text-gray-500 font-manrope text-xs">
            <a href="tel:+393518892962" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors"><Phone size={13} /> +39 351 889 2962</a>
            <a href="mailto:info@lapeschieraclub.it" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors"><Mail size={13} /> info@lapeschieraclub.it</a>
          </div>
        </div>
        <p className="text-center text-gray-600 font-manrope text-xs mt-5">
          La Peschiera Club &copy; {new Date().getFullYear()} — Valdengo (BI)
        </p>
      </footer>
    </div>
  );
}
